import styled from '@emotion/styled';
import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  colorAgentBubbleAssistant,
  colorAgentTextSecondary,
  colorNeutralDeepGray,
  colorPrimaryDeepBlue,
  fontFamilyMono,
  fontWeightSemibold,
  lineHeightRelaxed,
  radiusSm,
  space2,
  space3,
} from '../styles/tokens';

const MarkdownContainer = styled(Box)`
  font-size: 13px;
  line-height: ${lineHeightRelaxed};
  word-break: break-word;

  p {
    margin: 0 0 ${space2};
    &:last-child {
      margin-bottom: 0;
    }
  }

  code {
    background-color: ${colorAgentBubbleAssistant};
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 13px;
    font-family: ${fontFamilyMono};
  }

  pre {
    background-color: ${colorAgentBubbleAssistant};
    padding: ${space3};
    border-radius: ${radiusSm};
    overflow-x: auto;

    code {
      background: none;
      padding: 0;
    }
  }

  .table-wrapper {
    overflow-x: auto;
    margin: ${space2} 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
  }

  th,
  td {
    border: 1px solid ${colorNeutralDeepGray};
    padding: 4px 6px;
    text-align: left;
    font-size: 11px;
  }

  th {
    background-color: ${colorAgentBubbleAssistant};
    font-weight: ${fontWeightSemibold};
    white-space: nowrap;
  }

  ul,
  ol {
    margin: 4px 0;
    padding-left: 20px;
  }

  li {
    margin: 2px 0;
  }

  a {
    color: ${colorPrimaryDeepBlue};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  blockquote {
    border-left: 3px solid ${colorNeutralDeepGray};
    margin: ${space2} 0;
    padding: 4px ${space3};
    color: ${colorAgentTextSecondary};
  }
`;

interface AgentMarkdownProps {
  content: string;
}

function normalizeAgentResponse(text: string): string {
  let result = text.replace(/<thinking>[\s\S]*?<\/thinking>\s*/gi, '');
  result = result.trim();
  const responseMatch = result.match(/^<response>([\s\S]*)<\/response>\s*$/i);
  if (responseMatch) {
    result = responseMatch[1].trim();
  }
  const answerMatch = result.match(/^<answer>([\s\S]*)<\/answer>\s*$/i);
  if (answerMatch) {
    result = answerMatch[1].trim();
  }
  return result;
}

export function AgentMarkdown({ content }: AgentMarkdownProps) {
  const cleaned = normalizeAgentResponse(content);
  return (
    <MarkdownContainer>
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }: { href?: string; children?: ReactNode }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          table: ({ children }: { children?: ReactNode }) => (
            <div className="table-wrapper">
              <table>{children}</table>
            </div>
          ),
        }}
      >
        {cleaned}
      </Markdown>
    </MarkdownContainer>
  );
}
